import { Controller } from '@hotwired/stimulus';
import { visit } from '@hotwired/turbo';

// Recherche globale (admin et technicien) : ⌘K (Mac) /
// Ctrl K (autres systèmes, rappelé dans l'infobulle de la loupe) ou le
// bouton ouvrent la fenêtre (champ vide : fiches récentes chargées) ;
// résultats chargés dans leur Frame 250 ms après la frappe ; flèches pour
// choisir une ligne (aria-activedescendant), Entrée pour l'ouvrir (après les
// résultats à jour si une recherche est en cours ; action data-turbo-frame :
// dans la fenêtre de la page), Échap ou clic sur le fond pour fermer ;
// nombre de résultats annoncé (status) ; fenêtre fermée avant l'instantané
// de Turbo (retour arrière).
export default class extends Controller {
    static targets = ['dialog', 'form', 'input', 'frame', 'shortcut', 'status'];

    connect() {
        this.mac = /Mac|iPhone|iPad/.test(navigator.platform);
        this.shortcutTargets.forEach((button) => { button.title = `Rechercher (${this.mac ? '⌘K' : 'Ctrl K'})`; });
        this.beforeCache = () => this.close();
        document.addEventListener('turbo:before-cache', this.beforeCache);
    }

    disconnect() {
        clearTimeout(this.timer);
        document.removeEventListener('turbo:before-cache', this.beforeCache);
    }

    shortcut(event) {
        if (event.key?.toLowerCase() !== 'k' || event.altKey || event.shiftKey) return;
        if (!(this.mac ? event.metaKey : event.ctrlKey)) return;
        this.open(event);
    }

    open(event) {
        event?.preventDefault();
        if (!this.dialogTarget.open) this.dialogTarget.showModal();
        this.inputTarget.focus();
        this.inputTarget.select();
        // Champ vide : fiches récentes (à jour à chaque ouverture).
        if (this.inputTarget.value.trim() === '') this.submit();
    }

    close() {
        if (this.dialogTarget.open) this.dialogTarget.close();
    }

    // Clic sur le fond (la boîte elle-même, hors de son contenu).
    backdrop(event) {
        if (event.target === this.dialogTarget) this.close();
    }

    queue() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.submit(), 250);
    }

    submit() {
        clearTimeout(this.timer);
        this.timer = null;
        this.formTarget.requestSubmit();
    }

    // Entrée demandée pendant une recherche : seulement sur les résultats
    // de la saisie courante (une réponse plus ancienne peut arriver avant).
    frameLoaded() {
        this.select(-1);
        this.announce();
        if (!this.enterRequested) return;
        if (this.frameTarget.querySelector('[data-query]')?.dataset.query !== this.inputTarget.value.trim()) return;
        this.enterRequested = false;
        this.go();
    }

    navigate(event) {
        if (event.isComposing) return;
        const options = this.options();
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (options.length === 0) return;
            const step = event.key === 'ArrowDown' ? 1 : -1;
            this.select(((this.active ?? -1) + step + options.length) % options.length);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (this.timer || this.frameTarget.hasAttribute('busy')) {
                this.enterRequested = true;
                if (this.timer) this.submit();
                return;
            }
            this.go();
        }
    }

    go() {
        const options = this.options();
        const target = options[this.active] || options[0];
        if (!target) return;
        this.close();
        // Action ouverte dans la fenêtre de la page (data-turbo-frame).
        if (target.dataset.turboFrame) target.click();
        else visit(target.href);
    }

    select(index) {
        this.active = index;
        const options = this.options();
        options.forEach((option, position) => option.setAttribute('aria-selected', String(position === index)));
        const current = options[index];
        if (current) {
            this.inputTarget.setAttribute('aria-activedescendant', current.id);
            current.scrollIntoView({ block: 'nearest' });
        } else {
            this.inputTarget.removeAttribute('aria-activedescendant');
        }
    }

    // Résultats de la saisie (sans saisie : rien à annoncer, fiches récentes).
    announce() {
        if (!this.hasStatusTarget) return;
        const count = this.options().length;
        if (this.inputTarget.value.trim() === '') this.statusTarget.textContent = '';
        else this.statusTarget.textContent = count === 0 ? 'Aucun résultat' : `${count} résultat${count > 1 ? 's' : ''}`;
    }

    options() {
        return [...this.frameTarget.querySelectorAll('[role=option]')];
    }
}
